import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AppShell, TopHeader } from "../components/AppShell";
import { Button, Card, Pill, SectionLabel, Stat } from "../components/ui";
import { Ic } from "../components/icons";
import { useNav } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import {
  subscribeMyFiches,
  type FicheDoc,
  type FicheStatus,
} from "../lib/fiches";

type ChartBucket = "Jour" | "Semaine" | "Mois";

export default function Dashboard() {
  const { go } = useNav();
  const { user, loading: authLoading } = useAuth();
  const [fiches, setFiches] = useState<FicheDoc[]>([]);
  const [state, setState] = useState<"loading" | "loaded" | "anon">(
    user ? "loading" : "anon",
  );
  const [chartBucket, setChartBucket] = useState<ChartBucket>("Jour");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setFiches([]);
      setState("anon");
      return;
    }
    setState("loading");
    const unsub = subscribeMyFiches((list) => {
      setFiches(list);
      setState("loaded");
    });
    return () => unsub();
  }, [user, authLoading]);

  const stats = useMemo(() => computeStats(fiches), [fiches]);
  const activity = useMemo(
    () => buildActivity(fiches, chartBucket),
    [fiches, chartBucket],
  );
  const insights = useMemo(() => buildInsights(fiches, stats), [fiches, stats]);
  const recent = useMemo(() => fiches.slice(0, 7), [fiches]);

  const firstName = useMemo(() => pickFirstName(user), [user]);
  const periodLabel = useMemo(() => monthYearLabel(new Date()), []);

  return (
    <AppShell active="dashboard">
      <TopHeader />

      {/* Sticky new analysis button */}
      <button
        onClick={() => go("nouvelle")}
        className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-40 h-10 sm:h-12 px-4 sm:px-5 rounded-full bg-ink-900 text-white flex items-center gap-2 shadow-pop hover:bg-ink-800 text-[13px] sm:text-[13.5px]"
      >
        <Ic.Plus size={16} /> Nouvelle analyse
      </button>

      <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:py-8 page-enter">
        {/* Heading */}
        <div className="flex items-end justify-between gap-4 sm:gap-6 flex-wrap mb-6 sm:mb-8">
          <div>
            <SectionLabel>{periodLabel}</SectionLabel>
            <h1 className="mt-2 text-[24px] sm:text-[34px] font-semibold tracking-tight">
              Bonjour{firstName ? `, ${firstName}` : ""}{" "}
              <span className="font-serif italic font-normal text-ink-500">·</span>{" "}
              <span className="text-ink-600 font-normal">
                {headingTail(state, stats.thisMonthCount)}
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Pill tone="soft" icon={<Ic.Dot size={8} className="text-forest" />}>
              Tous les services opérationnels
            </Pill>
          </div>
        </div>

        {state === "anon" && <AnonState onNew={() => go("nouvelle")} />}
        {state === "loading" && <LoadingState />}
        {state === "loaded" && fiches.length === 0 && (
          <EmptyState onNew={() => go("nouvelle")} />
        )}

        {state === "loaded" && fiches.length > 0 && (
          <div className="grid grid-cols-12 gap-5">
            {/* Stats row */}
            <div className="col-span-12 grid grid-cols-12 gap-5">
              <div className="col-span-12 md:col-span-3">
                <Stat
                  label="Fiches générées"
                  value={String(stats.thisMonthCount)}
                  sub={deltaLabel(stats.thisMonthCount, stats.lastMonthCount, "vs. mois dernier")}
                  icon={<Ic.File size={16} />}
                  tone="lime"
                />
              </div>
              <div className="col-span-12 md:col-span-3">
                <Stat
                  label="Mandats signés"
                  value={String(stats.mandatsSignesTotal)}
                  sub={
                    stats.conversionRate == null
                      ? "aucun mandat envoyé"
                      : `taux de conversion ${stats.conversionRate} %`
                  }
                  icon={<Ic.TrendUp size={16} />}
                />
              </div>
              <div className="col-span-12 md:col-span-3">
                <Stat
                  label="Communes couvertes"
                  value={String(stats.communesCount)}
                  sub={
                    stats.firstFicheLabel
                      ? `depuis ${stats.firstFicheLabel}`
                      : "—"
                  }
                  icon={<Ic.Map size={16} />}
                />
              </div>
              <div className="col-span-12 md:col-span-3">
                <Stat
                  label="Potentiel moyen"
                  value={
                    stats.avgPotentiel == null
                      ? "—"
                      : `+${stats.avgPotentiel} m²`
                  }
                  sub={
                    stats.maxPotentiel == null
                      ? "règlement non disponible"
                      : `max ${stats.maxPotentiel} m² · ${stats.potentielSampleSize} fiches`
                  }
                  icon={<Ic.Grid size={16} />}
                />
              </div>
            </div>

            {/* Activity chart */}
            <Card className="col-span-12 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[15px] font-semibold tracking-tight">
                    Activité — {activity.title}
                  </div>
                  <div className="text-[12.5px] text-ink-500">
                    Fiches par {activity.unit}, réparties par type
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-ink-100 rounded-full p-1 text-[12px]">
                  {(["Jour", "Semaine", "Mois"] as ChartBucket[]).map((t) => (
                    <button
                      key={t}
                      onClick={() => setChartBucket(t)}
                      className={`px-3 h-7 rounded-full transition ${
                        chartBucket === t
                          ? "bg-paper shadow-card font-medium"
                          : "text-ink-600 hover:text-ink-900"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <ActivityChart buckets={activity.buckets} max={activity.max} />
              <div className="mt-4 grid grid-cols-3 gap-3">
                <Legend
                  color="#14161A"
                  label="Mandats"
                  value={String(activity.totals.mandat)}
                />
                <Legend
                  color="#D9EA2C"
                  label="Acheteurs"
                  value={String(activity.totals.acheteur)}
                  dot
                />
                <Legend
                  color="#A6ADB7"
                  label={`Moyenne ${activity.unit}`}
                  value={
                    activity.average == null
                      ? "—"
                      : activity.average.toFixed(1)
                  }
                  dashed
                />
              </div>
            </Card>

            {/* Fiches table */}
            <Card className="col-span-12 p-0 overflow-hidden">
              <div className="p-5 flex items-center justify-between border-b border-ink-200/70 gap-3 flex-wrap">
                <div>
                  <div className="text-[15px] font-semibold tracking-tight">
                    Mes dernières fiches
                  </div>
                  <div className="text-[12.5px] text-ink-500">
                    Les fiches sont conservées 12 mois. Téléchargez pour archive
                    longue.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Pill tone="soft">Tous · {stats.totalCount}</Pill>
                  <Pill tone="soft">Mandat · {stats.mandatCount}</Pill>
                  <Pill tone="soft">Acheteur · {stats.acheteurCount}</Pill>
                </div>
              </div>
              <FichesTable
                fiches={recent}
                onOpen={(id) => go("fiche", { ficheId: id })}
              />
              {fiches.length > recent.length && (
                <div className="p-4 border-t border-ink-200/70 text-center">
                  <Button
                    variant="soft"
                    size="sm"
                    onClick={() => go("fiches")}
                    iconRight={<Ic.Arrow size={13} />}
                  >
                    Voir les {fiches.length} fiches
                  </Button>
                </div>
              )}
            </Card>

            {/* Insights */}
            <Card className="col-span-12 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Ic.Sparkle size={15} className="text-ink-700" />
                <div className="text-[15px] font-semibold tracking-tight">
                  Insights
                </div>
              </div>
              {insights.length === 0 ? (
                <div className="text-[12.5px] text-ink-500">
                  Pas encore assez de données pour générer des insights. Créez
                  quelques fiches pour commencer.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {insights.map((ins, i) => (
                    <Insight
                      key={ins.title}
                      title={ins.title}
                      body={ins.body}
                      icon={ins.icon}
                      tone={i === 0 ? "lime" : undefined}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/* ------------- Dashboard bits ------------- */
const Legend = ({
  color,
  label,
  value,
  dot,
  dashed,
}: {
  color: string;
  label: string;
  value: string;
  dot?: boolean;
  dashed?: boolean;
}) => (
  <div className="flex items-center gap-2 text-[12px]">
    {dot ? (
      <span
        className="w-2.5 h-2.5 rounded-full"
        style={{ background: color }}
      />
    ) : dashed ? (
      <span
        className="w-4 h-0 border-t border-dashed"
        style={{ borderColor: color }}
      />
    ) : (
      <span
        className="w-4 h-[3px] rounded-full"
        style={{ background: color }}
      />
    )}
    <span className="text-ink-600">{label}</span>
    <span className="ml-auto font-medium tabular">{value}</span>
  </div>
);

const ActivityChart = ({
  buckets,
  max,
}: {
  buckets: ActivityBucket[];
  max: number;
}) => {
  const h = 180;
  const w = 700;
  const pad = 12;
  const safeMax = Math.max(max, 1);
  const bw = (w - pad * 2) / Math.max(buckets.length, 1);

  const avg =
    buckets.reduce((acc, b) => acc + b.mandat + b.acheteur, 0) /
    Math.max(buckets.length, 1);
  const avgY = h - (avg / safeMax) * (h - 20) - 10;

  const labelIndices = pickLabelIndices(buckets.length);

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-[200px]">
      {[0.25, 0.5, 0.75].map((r) => (
        <line
          key={r}
          x1={pad}
          x2={w - pad}
          y1={h - r * (h - 20) - 10}
          y2={h - r * (h - 20) - 10}
          stroke="rgba(20,22,26,0.08)"
          strokeWidth="1"
          strokeDasharray="2 3"
        />
      ))}
      {buckets.map((b, i) => {
        const x = pad + i * bw;
        const hm = (b.mandat / safeMax) * (h - 20);
        const ha = (b.acheteur / safeMax) * (h - 20);
        return (
          <g key={i}>
            {hm > 0 && (
              <rect
                x={x + 2}
                y={h - hm - 10}
                width={Math.max(bw - 6, 2)}
                height={hm}
                fill="#14161A"
                rx="2"
              />
            )}
            {ha > 0 && (
              <rect
                x={x + 2}
                y={h - hm - ha - 10}
                width={Math.max(bw - 6, 2)}
                height={ha}
                fill="#D9EA2C"
                rx="2"
              />
            )}
          </g>
        );
      })}
      {avg > 0 && (
        <line
          x1={pad}
          x2={w - pad}
          y1={avgY}
          y2={avgY}
          stroke="#A6ADB7"
          strokeWidth="1.5"
          strokeDasharray="3 3"
        />
      )}
      {labelIndices.map((i) => (
        <text
          key={i}
          x={pad + i * bw + bw / 2}
          y={h - 2}
          fontSize="9"
          textAnchor="middle"
          fill="#7A818C"
          fontFamily="JetBrains Mono"
        >
          {buckets[i]?.label ?? ""}
        </text>
      ))}
    </svg>
  );
};

const FichesTable = ({
  fiches,
  onOpen,
}: {
  fiches: FicheDoc[];
  onOpen: (id: string) => void;
}) => (
  <div className="overflow-x-auto">
    <table className="w-full text-[13px]">
      <thead>
        <tr className="text-ink-500 text-left">
          <th className="px-5 py-3 font-medium">ID</th>
          <th className="px-5 py-3 font-medium">Adresse</th>
          <th className="px-5 py-3 font-medium">Type</th>
          <th className="px-5 py-3 font-medium">Date</th>
          <th className="px-5 py-3 font-medium">Zone</th>
          <th className="px-5 py-3 font-medium">Potentiel</th>
          <th className="px-5 py-3 font-medium">Statut</th>
          <th className="px-5 py-3 font-medium w-[1%]"></th>
        </tr>
      </thead>
      <tbody>
        {fiches.map((f) => {
          const pot = computePotentialM2(f);
          const potText =
            pot == null ? "—" : pot > 0 ? `+${pot} m²` : "+0 m²";
          return (
            <tr
              key={f.id}
              onClick={() => onOpen(f.id)}
              className="border-t border-ink-200/70 hover:bg-ink-50/70 cursor-pointer"
            >
              <td className="px-5 py-3 font-mono text-[11.5px] text-ink-500">
                {f.id}
              </td>
              <td className="px-5 py-3">
                <div className="font-medium truncate max-w-[320px]">
                  {primaryAddress(f)}
                </div>
                <div className="text-[11.5px] text-ink-500 truncate max-w-[320px]">
                  {secondaryAddress(f)}
                </div>
              </td>
              <td className="px-5 py-3">
                <Pill tone={f.type === "Mandat" ? "lime" : "soft"}>
                  {f.type}
                </Pill>
              </td>
              <td className="px-5 py-3 text-ink-600">
                {formatDate(f.createdAt?.toDate() ?? null)}
              </td>
              <td className="px-5 py-3 font-mono">
                {f.zone?.libelle ?? "—"}
              </td>
              <td className="px-5 py-3 font-semibold tabular">{potText}</td>
              <td className="px-5 py-3">
                <StatusDot status={f.status} />
              </td>
              <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                <IconBtn
                  icon={<Ic.Eye size={13} />}
                  label="Voir"
                  onClick={() => onOpen(f.id)}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const StatusDot = ({ status }: { status: FicheStatus }) => {
  const color =
    status === "signé"
      ? "bg-forest"
      : status === "envoyé"
        ? "bg-ink-400"
        : "bg-amber-400";
  const label =
    status === "brouillon"
      ? "Brouillon"
      : status === "envoyé"
        ? "Envoyé"
        : "Signé";
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
      <span className="text-ink-700">{label}</span>
    </span>
  );
};

const IconBtn = ({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
}) => (
  <button
    title={label}
    onClick={onClick}
    className="w-7 h-7 rounded-full hover:bg-ink-100 grid place-items-center text-ink-600"
  >
    {icon}
  </button>
);

const Insight = ({
  title,
  body,
  icon,
  tone,
}: {
  title: string;
  body: string;
  icon: ReactNode;
  tone?: "lime";
}) => (
  <div
    className={`rounded-xl2 p-4 flex gap-3 ${
      tone === "lime" ? "bg-lime-200" : "bg-ink-50"
    }`}
  >
    <div
      className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${
        tone === "lime"
          ? "bg-ink-900 text-lime-300"
          : "bg-paper hairline text-ink-700"
      }`}
    >
      {icon}
    </div>
    <div>
      <div className="text-[13.5px] font-semibold">{title}</div>
      <div className="text-[12.5px] text-ink-700 mt-1 leading-relaxed">
        {body}
      </div>
    </div>
  </div>
);

/* ---------- States ---------- */

const LoadingState = () => (
  <Card className="p-14 grid place-items-center">
    <div className="text-center">
      <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto animate-pulse">
        <Ic.File size={18} className="text-ink-500" />
      </div>
      <div className="mt-3 text-[13px] text-ink-500">
        Chargement de votre tableau de bord…
      </div>
    </div>
  </Card>
);

const EmptyState = ({ onNew }: { onNew: () => void }) => (
  <Card className="p-14 text-center">
    <div className="w-14 h-14 rounded-full bg-lime-200 grid place-items-center mx-auto">
      <Ic.Wand size={18} />
    </div>
    <h2 className="mt-4 text-[22px] font-semibold tracking-tight">
      Aucune donnée pour le moment
    </h2>
    <p className="mt-2 text-[13px] text-ink-600 max-w-[460px] mx-auto leading-relaxed">
      Votre tableau de bord se remplira dès votre première fiche. Lancez une
      analyse pour voir apparaître vos stats, votre carte et vos insights.
    </p>
    <div className="mt-5 flex items-center justify-center gap-2">
      <Button variant="dark" onClick={onNew} iconRight={<Ic.Arrow size={13} />}>
        Nouvelle analyse
      </Button>
    </div>
  </Card>
);

const AnonState = ({ onNew }: { onNew: () => void }) => (
  <Card className="p-14 text-center">
    <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto">
      <Ic.Cross size={16} className="text-ink-500" />
    </div>
    <h2 className="mt-4 text-[22px] font-semibold tracking-tight">
      Connexion requise
    </h2>
    <p className="mt-2 text-[13px] text-ink-600 max-w-[460px] mx-auto leading-relaxed">
      Connectez-vous pour accéder à votre tableau de bord. Toutes vos fiches y
      seront agrégées en temps réel.
    </p>
    <div className="mt-5 flex items-center justify-center gap-2">
      <Button variant="dark" onClick={onNew} iconRight={<Ic.Arrow size={13} />}>
        Nouvelle analyse
      </Button>
    </div>
  </Card>
);

/* ---------- Helpers: stats ---------- */

type Stats = {
  totalCount: number;
  mandatCount: number;
  acheteurCount: number;
  thisMonthCount: number;
  lastMonthCount: number;
  mandatsSignesTotal: number;
  conversionRate: number | null;
  communesCount: number;
  firstFicheLabel: string | null;
  avgPotentiel: number | null;
  maxPotentiel: number | null;
  potentielSampleSize: number;
};

function computeStats(fiches: FicheDoc[]): Stats {
  const now = new Date();
  const startThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const startLast = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  let thisMonth = 0;
  let lastMonth = 0;
  let mandatCount = 0;
  let acheteurCount = 0;
  let mandatsSignesTotal = 0;
  let mandatsEnvoyes = 0;
  const communes = new Set<string>();
  let firstDate: Date | null = null;
  const potentials: number[] = [];

  for (const f of fiches) {
    const d = f.createdAt?.toDate() ?? null;
    if (d) {
      if (d >= startThis) thisMonth++;
      else if (d >= startLast) lastMonth++;
      if (!firstDate || d < firstDate) firstDate = d;
    }
    if (f.type === "Mandat") mandatCount++;
    else acheteurCount++;
    if (f.type === "Mandat") {
      if (f.status === "signé") mandatsSignesTotal++;
      if (f.status === "signé" || f.status === "envoyé") mandatsEnvoyes++;
    }
    if (f.geocode?.insee) communes.add(f.geocode.insee);
    else if (f.geocode?.city) communes.add(f.geocode.city);
    const p = computePotentialM2(f);
    if (p != null) potentials.push(p);
  }

  const conversionRate =
    mandatsEnvoyes > 0
      ? Math.round((mandatsSignesTotal / mandatsEnvoyes) * 100)
      : null;

  const avgPotentiel =
    potentials.length > 0
      ? Math.round(
          potentials.reduce((a, b) => a + b, 0) / potentials.length,
        )
      : null;
  const maxPotentiel =
    potentials.length > 0 ? Math.max(...potentials) : null;

  return {
    totalCount: fiches.length,
    mandatCount,
    acheteurCount,
    thisMonthCount: thisMonth,
    lastMonthCount: lastMonth,
    mandatsSignesTotal,
    conversionRate,
    communesCount: communes.size,
    firstFicheLabel: firstDate ? monthYearLabel(firstDate).toLowerCase() : null,
    avgPotentiel,
    maxPotentiel,
    potentielSampleSize: potentials.length,
  };
}

function headingTail(
  state: "loading" | "loaded" | "anon",
  count: number,
): string {
  if (state === "anon") return "connectez-vous pour accéder à vos analyses.";
  if (state === "loading") return "chargement de vos fiches…";
  if (count === 0) return "aucune fiche ce mois-ci.";
  if (count === 1) return "1 fiche ce mois-ci.";
  return `${count} fiches ce mois-ci.`;
}

function deltaLabel(
  current: number,
  previous: number,
  suffix: string,
): string {
  const diff = current - previous;
  if (diff === 0) return `stable ${suffix}`;
  const sign = diff > 0 ? "+" : "";
  return `${sign}${diff} ${suffix}`;
}

/* ---------- Helpers: activity chart ---------- */

type ActivityBucket = {
  label: string;
  mandat: number;
  acheteur: number;
};

type Activity = {
  buckets: ActivityBucket[];
  max: number;
  totals: { mandat: number; acheteur: number };
  average: number | null;
  title: string;
  unit: string;
};

function buildActivity(fiches: FicheDoc[], bucket: ChartBucket): Activity {
  const now = new Date();
  const buckets: ActivityBucket[] = [];

  if (bucket === "Jour") {
    const days = 30;
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      buckets.push({
        label: String(d.getDate()),
        mandat: 0,
        acheteur: 0,
      });
    }
    for (const f of fiches) {
      const d = f.createdAt?.toDate() ?? null;
      if (!d) continue;
      const diff = Math.floor((stripTime(now) - stripTime(d)) / 86_400_000);
      if (diff < 0 || diff >= days) continue;
      const idx = days - 1 - diff;
      if (f.type === "Mandat") buckets[idx].mandat++;
      else buckets[idx].acheteur++;
    }
    return finalizeActivity(buckets, "30 derniers jours", "jour");
  }

  if (bucket === "Semaine") {
    const weeks = 12;
    const start = startOfWeek(now);
    for (let i = weeks - 1; i >= 0; i--) {
      const d = new Date(start);
      d.setDate(start.getDate() - i * 7);
      buckets.push({
        label: `S${isoWeek(d)}`,
        mandat: 0,
        acheteur: 0,
      });
    }
    for (const f of fiches) {
      const d = f.createdAt?.toDate() ?? null;
      if (!d) continue;
      const ws = startOfWeek(d).getTime();
      const diffWeeks = Math.floor((start.getTime() - ws) / (7 * 86_400_000));
      if (diffWeeks < 0 || diffWeeks >= weeks) continue;
      const idx = weeks - 1 - diffWeeks;
      if (f.type === "Mandat") buckets[idx].mandat++;
      else buckets[idx].acheteur++;
    }
    return finalizeActivity(buckets, "12 dernières semaines", "semaine");
  }

  const months = 12;
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({
      label: monthShort(d),
      mandat: 0,
      acheteur: 0,
    });
  }
  for (const f of fiches) {
    const d = f.createdAt?.toDate() ?? null;
    if (!d) continue;
    const diffMonths =
      (now.getFullYear() - d.getFullYear()) * 12 +
      (now.getMonth() - d.getMonth());
    if (diffMonths < 0 || diffMonths >= months) continue;
    const idx = months - 1 - diffMonths;
    if (f.type === "Mandat") buckets[idx].mandat++;
    else buckets[idx].acheteur++;
  }
  return finalizeActivity(buckets, "12 derniers mois", "mois");
}

function finalizeActivity(
  buckets: ActivityBucket[],
  title: string,
  unit: string,
): Activity {
  let mandat = 0;
  let acheteur = 0;
  let max = 0;
  for (const b of buckets) {
    mandat += b.mandat;
    acheteur += b.acheteur;
    max = Math.max(max, b.mandat + b.acheteur);
  }
  const total = mandat + acheteur;
  const average = buckets.length > 0 ? total / buckets.length : null;
  return {
    buckets,
    max,
    totals: { mandat, acheteur },
    average,
    title,
    unit,
  };
}

function pickLabelIndices(length: number): number[] {
  if (length <= 6) return Array.from({ length }, (_, i) => i);
  const count = 6;
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    out.push(Math.round((i * (length - 1)) / (count - 1)));
  }
  return Array.from(new Set(out));
}

function stripTime(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function startOfWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = out.getDay();
  const offset = day === 0 ? 6 : day - 1; // Monday-first
  out.setDate(out.getDate() - offset);
  return out;
}

function isoWeek(d: Date): number {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/* ---------- Helpers: insights ---------- */

type InsightItem = { title: string; body: string; icon: ReactNode };

function buildInsights(fiches: FicheDoc[], stats: Stats): InsightItem[] {
  const out: InsightItem[] = [];
  const now = Date.now();
  const DAY = 86_400_000;

  // 1. High-potential mandat still in draft — most actionable.
  const draftMandatsHighPot = fiches
    .filter((f) => f.type === "Mandat" && f.status === "brouillon")
    .map((f) => ({ f, pot: computePotentialM2(f) ?? 0 }))
    .filter((x) => x.pot >= 30)
    .sort((a, b) => b.pot - a.pot);
  if (draftMandatsHighPot.length > 0) {
    const top = draftMandatsHighPot[0];
    out.push({
      title: "Mandat à fort potentiel à envoyer",
      body: `${primaryAddress(top.f)} — potentiel d'extension de +${top.pot} m². Un bel argument, mais la fiche est encore en brouillon.`,
      icon: <Ic.TrendUp size={14} />,
    });
  }

  // 2. Mandats sent >14 days ago without signature — relances.
  const stalePending = fiches.filter((f) => {
    if (f.type !== "Mandat" || f.status !== "envoyé") return false;
    const d = f.updatedAt?.toDate() ?? f.createdAt?.toDate() ?? null;
    if (!d) return false;
    return now - d.getTime() >= 14 * DAY;
  });
  if (stalePending.length > 0) {
    out.push({
      title: "Mandats à relancer",
      body: `${stalePending.length} mandat${stalePending.length > 1 ? "s envoyés" : " envoyé"} depuis +14 jours sans signature. Pensez à recontacter le vendeur.`,
      icon: <Ic.Mail size={14} />,
    });
  }

  // 3. Old drafts (>7 days) — to finalize or archive.
  const oldDrafts = fiches.filter((f) => {
    if (f.status !== "brouillon") return false;
    const d = f.createdAt?.toDate() ?? null;
    if (!d) return false;
    return now - d.getTime() >= 7 * DAY;
  });
  if (oldDrafts.length > 0) {
    out.push({
      title: "Brouillons à finaliser",
      body: `${oldDrafts.length} brouillon${oldDrafts.length > 1 ? "s" : ""} de plus de 7 jours. Envoyez-${oldDrafts.length > 1 ? "les" : "le"} ou archivez-${oldDrafts.length > 1 ? "les" : "le"} pour garder votre liste claire.`,
      icon: <Ic.File size={14} />,
    });
  }

  // 4. Divisible plots — commercial angle.
  const largeParcels = fiches.filter((f) => (f.parcel?.surface_m2 ?? 0) >= 500);
  if (largeParcels.length > 0) {
    out.push({
      title: "Division parcellaire à explorer",
      body: `${largeParcels.length} parcelle${largeParcels.length > 1 ? "s" : ""} de +500 m² dans votre portefeuille. Le scénario division est un levier de négociation.`,
      icon: <Ic.Grid size={14} />,
    });
  }

  // 5. Top zone by volume — if dominant.
  const zoneCounts = new Map<string, number>();
  for (const f of fiches) {
    const z = f.zone?.libelle;
    if (!z) continue;
    zoneCounts.set(z, (zoneCounts.get(z) ?? 0) + 1);
  }
  const topZone = [...zoneCounts.entries()].sort((a, b) => b[1] - a[1])[0];
  if (topZone && topZone[1] >= 3) {
    out.push({
      title: `Zone ${topZone[0]} : votre terrain de prédilection`,
      body: `${topZone[1]} fiches générées dans cette zone. Capitalisez sur votre connaissance du règlement pour les prochains mandats.`,
      icon: <Ic.Map size={14} />,
    });
  }

  // 6. Fallback — high-potential fiche exists.
  if (out.length === 0 && stats.maxPotentiel != null && stats.maxPotentiel > 0) {
    out.push({
      title: "Potentiel d'extension remarquable",
      body: `Une de vos fiches montre un potentiel de +${stats.maxPotentiel} m². Un bel argument pour le mandat.`,
      icon: <Ic.TrendUp size={14} />,
    });
  }

  return out.slice(0, 3);
}

/* ---------- Helpers: formatting + fiche derivations ---------- */

function computePotentialM2(f: FicheDoc): number | null {
  const empriseMaxPct = f.reglement?.emprise_au_sol_max_pct;
  const surface = f.parcel?.surface_m2;
  const existingEmprise = f.existing?.emprise_m2 ?? null;
  if (empriseMaxPct == null || !surface) return null;
  const maxEmprise = (empriseMaxPct / 100) * surface;
  if (existingEmprise == null) return Math.round(maxEmprise);
  const residual = maxEmprise - existingEmprise;
  return residual > 0 ? Math.round(residual) : 0;
}

function primaryAddress(f: FicheDoc): string {
  const label = f.geocode?.label ?? "";
  if (f.geocode?.city && label.includes(f.geocode.city)) {
    const idx = label.indexOf(f.geocode.city);
    return label.slice(0, idx).replace(/[,\s]+$/, "").trim() || label;
  }
  return label || f.title || f.id;
}

function secondaryAddress(f: FicheDoc): string {
  const parts = [f.geocode?.city, f.geocode?.postcode].filter(Boolean);
  return parts.join(" ");
}

function formatDate(d: Date | null): string {
  if (!d) return "à l'instant";
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function monthYearLabel(d: Date): string {
  const s = d.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function monthShort(d: Date): string {
  const s = d.toLocaleDateString("fr-FR", { month: "short" });
  return s.charAt(0).toUpperCase() + s.slice(1).replace(".", "");
}

function pickFirstName(user: { displayName?: string | null; email?: string | null } | null): string | null {
  if (!user) return null;
  if (user.displayName) {
    const first = user.displayName.trim().split(/\s+/)[0];
    if (first) return first;
  }
  if (user.email) {
    const local = user.email.split("@")[0];
    const first = local.replace(/[._-]+/g, " ").trim().split(/\s+/)[0];
    if (first) return first.charAt(0).toUpperCase() + first.slice(1);
  }
  return null;
}
